import React, { useEffect, useState } from 'react';

import { Link, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import InformationIcon from '@/assets/icons/information.svg?react';
import Button from '@/components/ui/buttons/Button';
import ErrorList from '@/components/ui/forms/ErrorList';
import Field from '@/components/ui/forms/Field';
import FormGroup from '@/components/ui/forms/FormGroup';
import Input from '@/components/ui/forms/Input';
import LabelWithStatus from '@/components/ui/forms/LabelWithStatus';
import useReactHookForm from '@/hooks/common/useReactHookForm';
import { type Student } from '@/interfaces/student';
import { SAPURI_EMAIL_REGEXP, HANKAKU_REGEXP } from '@/store/constants';

import styles from './InviteEmailInputForm.module.scss';

type FormValues = {
  email: string;
};

interface Props {
  student: Student;
  code: string;
  initialEmail?: string;
  onBack: () => void;
}

const InviteEmailInputForm: React.FC<Props> = ({
  student,
  code,
  initialEmail,
  onBack,
}) => {
  const navigate = useNavigate();
  const [formValues] = useState<FormValues>({
    email: initialEmail ?? '',
  });

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required('メールアドレスは必須です')
      .test('email-format', 'メールアドレスの形式が不正です', (value) => {
        if (!value) return true;
        if (/\.$/.test(value)) return false;
        return SAPURI_EMAIL_REGEXP.test(value);
      })
      .matches(HANKAKU_REGEXP, {
        message: 'メールアドレスは半角英数字で入力してください',
        excludeEmptyString: true,
      }),
  });

  const {
    registerWithInnerRef,
    handleSubmit,
    formState: {
      isSubmitting,
      isValid,
      validFields,
      touchedFields,
      errorMessages,
    },
    trigger,
    watch,
  } = useReactHookForm<FormValues>({
    validationSchema,
    defaultValues: formValues,
  });

  const emailValue = watch('email');

  useEffect(() => {
    if (emailValue) {
      void trigger('email');
    }
  }, [emailValue, trigger]);

  const handleFormSubmit = (data: FormValues) => {
    navigate(`/mypage/invite/${code}/confirm`, {
      state: { email: data.email },
    });
  };

  const inputDisabled = isSubmitting;
  const buttonDisabled = !isValid || isSubmitting;

  return (
    <form
      className={styles.InviteEmailInputForm}
      onSubmit={(e) => {
        e.preventDefault();
        void handleSubmit(handleFormSubmit)(e);
      }}
    >
      <div className={styles.InviteEmailInputForm__StudentInfo}>
        <div className={styles.InviteEmailInputForm__StudentInfo__Title}>
          招待する方のメールアドレスを入力してください。
        </div>
        <div className={styles.InviteEmailInputForm__StudentInfoLabel}>
          生徒名
        </div>
        <div className={styles.InviteEmailInputForm__StudentInfoValue}>
          <p>{student.name}</p>
        </div>
      </div>
      <FormGroup>
        <Field className={styles.InviteEmailInputForm__Field}>
          <LabelWithStatus required valid={validFields.email}>
            招待する方のメールアドレス
          </LabelWithStatus>
          <div className={styles.InviteEmailInputForm__InputWrapper}>
            <Input
              id="email"
              type="email"
              placeholder="メールアドレスを入力"
              disabled={inputDisabled}
              {...registerWithInnerRef('email')}
            />
          </div>
          {touchedFields.email && errorMessages.email && (
            <ErrorList errors={errorMessages.email} />
          )}
          {student.max_parents_count != null && (
            <ul className={styles.Comment}>
              <li>
                <p>
                  {`生徒1人に対し、保護者は自身を含め最大${student.max_parents_count}人まで紐付けできます。`}
                </p>
              </li>
            </ul>
          )}
          <div className={styles.Notification}>
            <InformationIcon className={styles.Notification__Icon} />
            <p>
              招待する方は、あなたと同じ操作ができるようになります。これには、あなたと生徒の紐付けを解除する操作も含まれます。
            </p>
          </div>
        </Field>
      </FormGroup>
      <div className={styles.InviteEmailInputForm__Buttons}>
        <Button
          type="submit"
          color="primary"
          size="m"
          disabled={buttonDisabled}
        >
          確認画面へ
        </Button>
        <Button
          tag={Link}
          to="/mypage/profile"
          color="link"
          size="m"
          onClick={onBack}
        >
          ＜戻る
        </Button>
      </div>
    </form>
  );
};

export default InviteEmailInputForm;
